import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentsAPI } from '../api';

interface CardPaymentFormProps {
  amount: number;
  listingId?: number;
  deliveryAddress?: string;
  addressData?: { line1: string; city: string; state: string; postal_code: string; country: string; formatted: string } | null;
  deliveryInstructions?: string;
  deliveryPreference?: 'leave_at_door' | 'meet_at_door';
  /** Price breakdown sent to server so purchase record matches what was charged */
  priceBreakdown?: { item_price: number; shipping_fee: number; sales_tax: number; total_amount: number; quantity?: number; listings?: Array<{ listing_id: number; quantity: number }> };
  onSuccess: () => void;
  onError: (error: string) => void;
}

const CardPaymentForm: React.FC<CardPaymentFormProps> = ({
  amount,
  listingId,
  deliveryAddress,
  addressData,
  deliveryInstructions,
  deliveryPreference = 'leave_at_door',
  priceBreakdown,
  onSuccess,
  onError,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  // Stripe is null initially while loading - only show error after delay (give it time to load)
  useEffect(() => {
    if (stripe) return;
    const timer = setTimeout(() => {
      onError('Stripe is not loaded. Please refresh the page or use cash payment.');
    }, 4000);
    return () => clearTimeout(timer);
  }, [stripe, onError]);
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [country, setCountry] = useState('US');
  const [zipCode, setZipCode] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<any[]>([]);
  const zipCodeInputRef = useRef<HTMLInputElement>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);

  // Load saved payment methods
  React.useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const data = await paymentsAPI.getPaymentMethods();
        setSavedPaymentMethods(data.paymentMethods || []);
        if (data.paymentMethods && data.paymentMethods.length > 0) {
          // Auto-select first saved card if available
          setSelectedPaymentMethod(data.paymentMethods[0].id);
          setShowNewCardForm(false);
        } else {
          setShowNewCardForm(true);
        }
      } catch (error: any) {
        console.error('Error loading payment methods:', error);
        // If error, show new card form
        setShowNewCardForm(true);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    loadPaymentMethods();
  }, []);

  React.useEffect(() => {
    // Create payment intent when component mounts or when amount/listingId/saveCard changes
    const createPaymentIntent = async () => {
      try {
        const result = await paymentsAPI.createPaymentIntent(amount, listingId, saveCard);
        setClientSecret(result.clientSecret);
      } catch (error: any) {
        onError(error.response?.data?.error || 'Failed to initialize payment');
      }
    };

    if (amount > 0 && !loadingPaymentMethods) {
      createPaymentIntent();
    }
  }, [amount, listingId, saveCard, loadingPaymentMethods]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !clientSecret) {
      onError('Stripe not loaded');
      return;
    }

    setProcessing(true);

    try {
      let paymentIntent;

      // If using a saved payment method
      if (selectedPaymentMethod && !showNewCardForm) {
        // Use saved payment method
        const { error: stripeError, paymentIntent: pi } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: selectedPaymentMethod,
        });

        if (stripeError) {
          onError(stripeError.message || 'Payment failed');
          setProcessing(false);
          return;
        }

        paymentIntent = pi;
      } else {
        // Use new card - validate all elements are present
        const cardNumberElement = elements?.getElement(CardNumberElement);
        const cardExpiryElement = elements?.getElement(CardExpiryElement);
        const cardCvcElement = elements?.getElement(CardCvcElement);
        
        if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
          console.error('Missing card elements:', { cardNumberElement: !!cardNumberElement, cardExpiryElement: !!cardExpiryElement, cardCvcElement: !!cardCvcElement });
          onError('Please complete all card fields');
          setProcessing(false);
          return;
        }

        // Validate zipcode - make it required for US
        if (country === 'US' && (!zipCode || zipCode.trim().length < 5)) {
          onError('Please enter a valid ZIP code');
          setProcessing(false);
          return;
        }

        console.log('Confirming payment with:', { 
          hasClientSecret: !!clientSecret, 
          country, 
          zipCode: zipCode || 'none',
          saveCard 
        });

        try {
          // When using separate card elements, we can use confirmCardPayment directly
          // by passing the cardNumberElement - Stripe Elements will automatically combine
          // all card fields (number, expiry, CVC) from the separate elements
          const { error: stripeError, paymentIntent: pi } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: cardNumberElement,
              billing_details: {
                address: {
                  country: country,
                  postal_code: zipCode || undefined,
                },
              },
            },
            setup_future_usage: saveCard ? 'off_session' : undefined,
          });

          if (stripeError) {
            console.error('Stripe payment confirmation error:', {
              type: stripeError.type,
              code: stripeError.code,
              message: stripeError.message,
              decline_code: stripeError.decline_code,
              param: stripeError.param,
            });
            onError(stripeError.message || 'Payment failed');
            setProcessing(false);
            return;
          }

          if (!pi) {
            console.error('No payment intent returned from confirmCardPayment');
            onError('Payment confirmation failed - no payment intent returned');
            setProcessing(false);
            return;
          }

          paymentIntent = pi;
        } catch (err: any) {
          console.error('Exception during payment confirmation:', err);
          onError(err.message || 'An unexpected error occurred during payment');
          setProcessing(false);
          return;
        }
      }

      // Accept both 'succeeded' (already captured) and 'requires_capture' (authorized, will be captured when shipped)
      if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture')) {
        console.log('Payment intent status:', paymentIntent.status);
        console.log('Confirming payment on backend with:', { 
          paymentIntentId: paymentIntent.id, 
          listingId, 
          deliveryAddress: deliveryAddress || 'none' 
        });
        
        try {
          // Confirm payment on backend and create purchase record (payment will be captured when item is shipped)
          await paymentsAPI.confirmPayment(paymentIntent.id, listingId, deliveryAddress, addressData, deliveryInstructions, deliveryPreference, priceBreakdown);
        } catch (apiError: any) {
          console.error('Backend API error:', {
            status: apiError.response?.status,
            statusText: apiError.response?.statusText,
            data: apiError.response?.data,
            message: apiError.message,
          });
          onError(apiError.response?.data?.error || apiError.message || 'Failed to confirm payment on server');
          setProcessing(false);
          return;
        }
        
        // Reload payment methods if card was saved
        if (saveCard || showNewCardForm) {
          try {
            const data = await paymentsAPI.getPaymentMethods();
            setSavedPaymentMethods(data.paymentMethods || []);
          } catch (err) {
            console.error('Error reloading payment methods:', err);
          }
        }
        
        onSuccess();
      } else {
        onError('Payment not completed');
        setProcessing(false);
      }
    } catch (error: any) {
      console.error('Payment processing error:', error);
      onError(error.message || 'Payment processing failed');
      setProcessing(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to remove this saved card?')) {
      return;
    }

    try {
      await paymentsAPI.deletePaymentMethod(paymentMethodId);
      const updated = savedPaymentMethods.filter(pm => pm.id !== paymentMethodId);
      setSavedPaymentMethods(updated);
      if (selectedPaymentMethod === paymentMethodId) {
        if (updated.length > 0) {
          setSelectedPaymentMethod(updated[0].id);
        } else {
          setSelectedPaymentMethod(null);
          setShowNewCardForm(true);
        }
      }
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to delete payment method');
    }
  };

  const formatCardBrand = (brand: string) => {
    const brands: { [key: string]: string } = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
      jcb: 'JCB',
      diners: 'Diners Club',
      unionpay: 'UnionPay',
    };
    return brands[brand.toLowerCase()] || brand;
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  if (loadingPaymentMethods) {
    return (
      <div className="text-center py-4">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <p className="mt-2 text-sm text-gray-600">Loading payment methods...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Saved Payment Methods - Show compact view if card is already selected */}
      {savedPaymentMethods.length > 0 && !showNewCardForm && (
        <div className="space-y-2">
          {savedPaymentMethods.length > 1 ? (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select a saved card
              </label>
              {savedPaymentMethods.map((pm) => (
                <div
                  key={pm.id}
                  onClick={() => {
                    setSelectedPaymentMethod(pm.id);
                    // Recreate payment intent with new card selection
                    paymentsAPI.createPaymentIntent(amount, listingId, false).then(result => {
                      setClientSecret(result.clientSecret);
                    }).catch(err => {
                      console.error('Error creating payment intent:', err);
                    });
                  }}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                    selectedPaymentMethod === pm.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        checked={selectedPaymentMethod === pm.id}
                        onChange={() => {
                          setSelectedPaymentMethod(pm.id);
                          paymentsAPI.createPaymentIntent(amount, listingId, false).then(result => {
                            setClientSecret(result.clientSecret);
                          }).catch(err => {
                            console.error('Error creating payment intent:', err);
                          });
                        }}
                        className="h-4 w-4 text-green-600 focus:ring-green-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatCardBrand(pm.card.brand)} •••• {pm.card.last4}
                        </p>
                        <p className="text-sm text-gray-500">
                          Expires {pm.card.exp_month}/{pm.card.exp_year}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleDeletePaymentMethod(pm.id, e)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setShowNewCardForm(true);
                  setSelectedPaymentMethod(null);
                }}
                className="text-green-600 hover:text-green-700 text-sm font-medium"
              >
+ Use a different card
              </button>
            </>
          ) : (
            // Single saved card - show compact view
            <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {formatCardBrand(savedPaymentMethods[0].card.brand)} •••• {savedPaymentMethods[0].card.last4}
                  </p>
                  <p className="text-sm text-gray-500">
                    Expires {savedPaymentMethods[0].card.exp_month}/{savedPaymentMethods[0].card.exp_year}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={(e) => handleDeletePaymentMethod(savedPaymentMethods[0].id, e)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewCardForm(true);
                      setSelectedPaymentMethod(null);
                    }}
                    className="text-green-600 hover:text-green-700 text-sm font-medium"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Card Form */}
      {showNewCardForm && (
        <div className="space-y-4">
          {savedPaymentMethods.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setShowNewCardForm(false);
                if (savedPaymentMethods.length > 0) {
                  setSelectedPaymentMethod(savedPaymentMethods[0].id);
                }
              }}
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              ← Use a saved card
            </button>
          )}
          {/* Card Number */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card number
            </label>
            <div className="relative">
              <div className="border border-gray-300 rounded-lg p-3 bg-white">
                <CardNumberElement 
                  options={{
                    ...cardElementOptions,
                    showIcon: false, // Hide autofill badge
                  }} 
                />
              </div>
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Scan card"
                onClick={() => {
                  // Trigger card scanning (Stripe Elements handles this automatically when available)
                  const cardNumberElement = elements?.getElement(CardNumberElement);
                  if (cardNumberElement) {
                    cardNumberElement.focus();
                  }
                }}
              >
                <i className="ri-camera-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Expiration Date and Security Code */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiration date
              </label>
              <div className="border border-gray-300 rounded-lg p-3 bg-white">
                <CardExpiryElement options={cardElementOptions} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Security code
              </label>
              <div className="border border-gray-300 rounded-lg p-3 bg-white">
                <CardCvcElement options={cardElementOptions} />
              </div>
            </div>
          </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="MX">Mexico</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="IT">Italy</option>
            <option value="ES">Spain</option>
            <option value="NL">Netherlands</option>
            <option value="BE">Belgium</option>
            <option value="CH">Switzerland</option>
            <option value="AT">Austria</option>
            <option value="SE">Sweden</option>
            <option value="NO">Norway</option>
            <option value="DK">Denmark</option>
            <option value="FI">Finland</option>
            <option value="IE">Ireland</option>
            <option value="PT">Portugal</option>
            <option value="GR">Greece</option>
            <option value="PL">Poland</option>
            <option value="CZ">Czech Republic</option>
            <option value="HU">Hungary</option>
            <option value="RO">Romania</option>
            <option value="BG">Bulgaria</option>
            <option value="HR">Croatia</option>
            <option value="SK">Slovakia</option>
            <option value="SI">Slovenia</option>
            <option value="EE">Estonia</option>
            <option value="LV">Latvia</option>
            <option value="LT">Lithuania</option>
            <option value="LU">Luxembourg</option>
            <option value="MT">Malta</option>
            <option value="CY">Cyprus</option>
            <option value="JP">Japan</option>
            <option value="KR">South Korea</option>
            <option value="CN">China</option>
            <option value="IN">India</option>
            <option value="BR">Brazil</option>
            <option value="AR">Argentina</option>
            <option value="CL">Chile</option>
            <option value="CO">Colombia</option>
            <option value="PE">Peru</option>
            <option value="VE">Venezuela</option>
            <option value="EC">Ecuador</option>
            <option value="UY">Uruguay</option>
            <option value="PY">Paraguay</option>
            <option value="BO">Bolivia</option>
            <option value="CR">Costa Rica</option>
            <option value="PA">Panama</option>
            <option value="GT">Guatemala</option>
            <option value="HN">Honduras</option>
            <option value="NI">Nicaragua</option>
            <option value="SV">El Salvador</option>
            <option value="DO">Dominican Republic</option>
            <option value="PR">Puerto Rico</option>
            <option value="JM">Jamaica</option>
            <option value="TT">Trinidad and Tobago</option>
            <option value="BS">Bahamas</option>
            <option value="BB">Barbados</option>
            <option value="BZ">Belize</option>
            <option value="GY">Guyana</option>
            <option value="SR">Suriname</option>
            <option value="GF">French Guiana</option>
            <option value="ZA">South Africa</option>
            <option value="EG">Egypt</option>
            <option value="NG">Nigeria</option>
            <option value="KE">Kenya</option>
            <option value="GH">Ghana</option>
            <option value="TZ">Tanzania</option>
            <option value="UG">Uganda</option>
            <option value="ET">Ethiopia</option>
            <option value="DZ">Algeria</option>
            <option value="MA">Morocco</option>
            <option value="TN">Tunisia</option>
            <option value="LY">Libya</option>
            <option value="SD">Sudan</option>
            <option value="AO">Angola</option>
            <option value="MZ">Mozambique</option>
            <option value="ZM">Zambia</option>
            <option value="ZW">Zimbabwe</option>
            <option value="BW">Botswana</option>
            <option value="NA">Namibia</option>
            <option value="MW">Malawi</option>
            <option value="RW">Rwanda</option>
            <option value="BI">Burundi</option>
            <option value="DJ">Djibouti</option>
            <option value="ER">Eritrea</option>
            <option value="SO">Somalia</option>
            <option value="SS">South Sudan</option>
            <option value="CF">Central African Republic</option>
            <option value="TD">Chad</option>
            <option value="NE">Niger</option>
            <option value="ML">Mali</option>
            <option value="BF">Burkina Faso</option>
            <option value="SN">Senegal</option>
            <option value="GM">Gambia</option>
            <option value="GW">Guinea-Bissau</option>
            <option value="GN">Guinea</option>
            <option value="SL">Sierra Leone</option>
            <option value="LR">Liberia</option>
            <option value="CI">Ivory Coast</option>
            <option value="TG">Togo</option>
            <option value="BJ">Benin</option>
            <option value="CM">Cameroon</option>
            <option value="GQ">Equatorial Guinea</option>
            <option value="GA">Gabon</option>
            <option value="CG">Republic of the Congo</option>
            <option value="CD">Democratic Republic of the Congo</option>
            <option value="ST">São Tomé and Príncipe</option>
            <option value="SC">Seychelles</option>
            <option value="MU">Mauritius</option>
            <option value="KM">Comoros</option>
            <option value="MG">Madagascar</option>
            <option value="RE">Réunion</option>
            <option value="YT">Mayotte</option>
            <option value="SH">Saint Helena</option>
            <option value="IO">British Indian Ocean Territory</option>
            <option value="AE">United Arab Emirates</option>
            <option value="SA">Saudi Arabia</option>
            <option value="KW">Kuwait</option>
            <option value="QA">Qatar</option>
            <option value="BH">Bahrain</option>
            <option value="OM">Oman</option>
            <option value="YE">Yemen</option>
            <option value="IQ">Iraq</option>
            <option value="IR">Iran</option>
            <option value="IL">Israel</option>
            <option value="JO">Jordan</option>
            <option value="LB">Lebanon</option>
            <option value="SY">Syria</option>
            <option value="TR">Turkey</option>
            <option value="CY">Cyprus</option>
            <option value="AM">Armenia</option>
            <option value="AZ">Azerbaijan</option>
            <option value="GE">Georgia</option>
            <option value="KZ">Kazakhstan</option>
            <option value="UZ">Uzbekistan</option>
            <option value="TM">Turkmenistan</option>
            <option value="TJ">Tajikistan</option>
            <option value="KG">Kyrgyzstan</option>
            <option value="AF">Afghanistan</option>
            <option value="PK">Pakistan</option>
            <option value="BD">Bangladesh</option>
            <option value="LK">Sri Lanka</option>
            <option value="MV">Maldives</option>
            <option value="NP">Nepal</option>
            <option value="BT">Bhutan</option>
            <option value="MM">Myanmar</option>
            <option value="TH">Thailand</option>
            <option value="LA">Laos</option>
            <option value="KH">Cambodia</option>
            <option value="VN">Vietnam</option>
            <option value="MY">Malaysia</option>
            <option value="SG">Singapore</option>
            <option value="BN">Brunei</option>
            <option value="ID">Indonesia</option>
            <option value="PH">Philippines</option>
            <option value="TL">East Timor</option>
            <option value="PG">Papua New Guinea</option>
            <option value="FJ">Fiji</option>
            <option value="NC">New Caledonia</option>
            <option value="PF">French Polynesia</option>
            <option value="WS">Samoa</option>
            <option value="TO">Tonga</option>
            <option value="VU">Vanuatu</option>
            <option value="SB">Solomon Islands</option>
            <option value="KI">Kiribati</option>
            <option value="TV">Tuvalu</option>
            <option value="NR">Nauru</option>
            <option value="PW">Palau</option>
            <option value="FM">Micronesia</option>
            <option value="MH">Marshall Islands</option>
            <option value="NZ">New Zealand</option>
            <option value="RU">Russia</option>
            <option value="UA">Ukraine</option>
            <option value="BY">Belarus</option>
            <option value="MD">Moldova</option>
            <option value="AL">Albania</option>
            <option value="MK">North Macedonia</option>
            <option value="ME">Montenegro</option>
            <option value="RS">Serbia</option>
            <option value="BA">Bosnia and Herzegovina</option>
            <option value="XK">Kosovo</option>
            <option value="IS">Iceland</option>
            <option value="GL">Greenland</option>
            <option value="FO">Faroe Islands</option>
            <option value="SJ">Svalbard and Jan Mayen</option>
            <option value="AX">Åland Islands</option>
            <option value="GI">Gibraltar</option>
            <option value="AD">Andorra</option>
            <option value="MC">Monaco</option>
            <option value="SM">San Marino</option>
            <option value="VA">Vatican City</option>
            <option value="LI">Liechtenstein</option>
            <option value="JE">Jersey</option>
            <option value="GG">Guernsey</option>
            <option value="IM">Isle of Man</option>
            <option value="MT">Malta</option>
            <option value="CY">Cyprus</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zip/Postal Code
          </label>
          <input
            ref={zipCodeInputRef}
            type="text"
            value={zipCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 5);
              setZipCode(value);
            }}
            placeholder="12345"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
            maxLength={5}
          />
        </div>
      </div>
      
      <div className="flex items-center">
        <input
          type="checkbox"
          id="saveCard"
          checked={saveCard}
          onChange={(e) => setSaveCard(e.target.checked)}
          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
        />
        <label htmlFor="saveCard" className="ml-2 block text-sm text-gray-700">
          Save card for future purchases
        </label>
      </div>
      
          <button
            type="submit"
            disabled={!stripe || processing || !clientSecret || (showNewCardForm && !zipCode.trim())}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
          </button>
        </div>
      )}

      {/* Submit button for saved cards */}
      {!showNewCardForm && savedPaymentMethods.length > 0 && (
        <button
          type="submit"
          disabled={!stripe || processing || !clientSecret || !selectedPaymentMethod}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
        </button>
      )}
    </form>
  );
};

export default CardPaymentForm;

